/**
 * The bounded wait for the login session on a hard page load (request-auth phase 4, prototype 2026-10-19).
 *
 * ── Why ───────────────────────────────────────────────────────────────────────────────────────────
 * The token interceptor never waits for Firebase (src/plugins/authTokenInterceptor.js: "ZERO WAIT"), so on a hard page load the
 * first-wave calls go out before Firebase has restored the session and carry no token. MEASURED 2026-10-19 on dev (10 hard loads
 * of /app/campaigns, real Chrome): the first-wave staff GETs fire ~700-950 ms after navigation tokenless, and the first request
 * carrying a token follows 131-190 ms later (median ~160 ms). Today that is harmless (nothing enforces). The day a backend route
 * enforces the login check, every hard load would be refused its first wave. This module closes that gap.
 *
 * ── The rule ──────────────────────────────────────────────────────────────────────────────────────
 * A request the token policy WOULD tag (wouldAttachIfSignedIn) that is fired before Firebase has said who is signed in is HELD
 * until the session is known, for at most SESSION_WAIT_CAP_MS counted from app start (not per request). Then it goes out, with the
 * token if there is one. Everything else — requests the policy never tags, requests after the session is known (including
 * "nobody is signed in"), requests after the cap — is untouched and sent synchronously, exactly as today.
 *
 * ── Slow restore (2026-11-04) ─────────────────────────────────────────────────────────────────────
 * The 2 s above covers a normal restore. A returning user's stored token is usually more than an hour old, so Firebase must refresh it
 * over the network before it can say who is signed in; on a slow connection that takes longer than 2 s and the first call used to go
 * out tokenless (reproduced on the live bundle: 1 s refresh → token; 3.5 s → 401). So when the 2 s cap passes with Firebase still
 * silent and the tab believes it is logged in (`expectSession`), the request keeps waiting up to SESSION_REFRESH_WAIT_CAP_MS from app
 * start — and is released the instant Firebase answers (a token, or "nobody"). Nobody-believing tabs are unchanged.
 *
 * ── Why it still honours "page load not slower" ─────────────────────────────────────────────────
 * It is only the first calls of a hard load, only the ones that would have been refused anyway, only until Firebase answers
 * (~0.2 s measured), and never longer than the cap even if Firebase never answers. In-app navigation never waits.
 *
 * Plain functions with injectable clock/timers so cypress/node-tests/auth-session-gate.test.js runs the exact shipped file.
 * The wiring (Firebase listener + axios registration) stays in src/plugins/authTokenInterceptor.js.
 */

import { applyAuthHeader, decideAuthHeader, wouldAttachIfSignedIn } from '@/utils/authHeaderPolicy';

// Longest a request waits for a fresh login token when the user is signed in but the cached token is unusable (2026-11-03).
// Counted per request, from the moment it is fired. A refresh that fails or hangs sends the request tokenless, as it always was.
export const REFRESH_WAIT_CAP_MS = 2000;

// Longest a request is held for the session. Counted from the gate's creation (app start), so a hung Firebase delays only the
// first calls, once. The measured need is ~0.2 s warm; the rest is headroom for a token refresh over a slow network.
export const SESSION_WAIT_CAP_MS = 2000;

// Longest a request is held when the 2 s cap passed and this tab BELIEVES it is logged in but Firebase has not answered yet
// (2026-11-04). Counted from app start like the cap above. A returning user's stored token is usually more than an hour old, so
// restoring the session needs a NETWORK refresh first: reproduced on the live bundle — refresh delayed 1 s → token sent; delayed
// 3.5 s → the first call went out tokenless → `get-credit-balance` 401 → Lumigo alert. The hold ends the instant Firebase answers
// (a token OR "nobody"), so only a slow restore waits at all; this is only the ceiling for one that never answers.
export const SESSION_REFRESH_WAIT_CAP_MS = 8000;

/**
 * settle()   — Firebase has answered (a user with a token, or nobody, or a failure): nothing waits any more.
 * isSettled() — has that happened.
 * wait(capOverrideMs) — resolves 'settled' when it does, or 'cap' when the cap from creation is reached (the gate's own capMs, or
 *               capOverrideMs when one is given — still counted from creation). Never rejects.
 */
export function createSessionGate({ capMs = SESSION_WAIT_CAP_MS, now = Date.now, setTimer = setTimeout, clearTimer = clearTimeout } = {}) {
  const startedAt = now();
  let settled = false;
  let resolveSettled;
  const settledPromise = new Promise((resolve) => { resolveSettled = resolve; });
  return {
    settle() {
      if (settled) return;
      settled = true;
      resolveSettled();
    },
    isSettled() { return settled; },
    wait(capOverrideMs) {
      if (settled) return Promise.resolve('settled');
      const cap = typeof capOverrideMs === 'number' && capOverrideMs > 0 ? capOverrideMs : capMs;
      const left = startedAt + cap - now();
      if (left <= 0) return Promise.resolve('cap');
      return new Promise((resolve) => {
        const timer = setTimer(() => resolve('cap'), left);
        settledPromise.then(() => { clearTimer(timer); resolve('settled'); });
      });
    },
  };
}

/**
 * The axios request-interceptor body: applyAuthHeader, preceded by the bounded wait when (and only when) the request would carry
 * a token but the session is not known yet.
 *   gate    — a createSessionGate()
 *   apiBase — the env API base
 *   getCtx  — () => { apiBase, token, expiresAt, now, signedIn } read fresh each call (the token may arrive while a request waits)
 *   hold    — optional deadSessionHold (src/utils/deadSessionHold.js): while it is holding (Firebase said "nobody" on a tab that
 *             believes it is logged in), a request the policy would tag also waits for the session-health guard's verdict.
 *             Absent / not holding ⇒ exactly the behaviour above.
 *   expectSession — optional () => boolean: "this tab believes it is logged in" (the app's own `authenticated` flag). When the 2 s hold
 *             ends with Firebase STILL silent and this says yes (or a user is already visible), the request keeps waiting for the
 *             answer, up to sessionRefreshCapMs from app start — a slow token refresh must not turn the first call into a 401.
 *             Absent / false / throws ⇒ the 2 s behaviour exactly.
 *   refresh — optional () => Promise<{ token, expiresAt } | null> (src/utils/authTokenRefresh.js). When the user is signed in but the
 *             cached token is unusable (expired / inside the skew / blank) and the request would be tagged, the request waits for it,
 *             at most refreshCapMs, then goes out WITH the fresh token — or as today if it failed or timed out. Absent ⇒ no such wait.
 * Returns the config (synchronously) or a Promise of it. Never throws, never rejects: on any surprise the request goes out as today.
 */
export function createAuthRequestHandler({
  gate, apiBase, getCtx, hold, refresh, expectSession, refreshCapMs = REFRESH_WAIT_CAP_MS,
  sessionRefreshCapMs = SESSION_REFRESH_WAIT_CAP_MS, setTimer = setTimeout, clearTimer = clearTimeout,
}) {
  const holding = () => !!(hold && hold.isHolding());

  // The 2 s hold ended and Firebase has still not answered: is it worth holding on? Only for a tab that believes it is logged in
  // (or whose user is already restored): its token is being fetched, and a request sent now is a guaranteed 401 on an enforced route.
  function worthHoldingOn() {
    try {
      if (gate.isSettled()) return false;
      if (expectSession && expectSession()) return true;
      const ctx = getCtx();
      return !!(ctx && ctx.signedIn);
    } catch (_e) {
      return false;
    }
  }

  // After the session gate: past its 2 s cap with Firebase still silent → keep holding (bounded, ends the instant it answers).
  const afterGate = (config, how) => (how === 'cap' && worthHoldingOn()
    ? gate.wait(sessionRefreshCapMs).then(() => afterHold(config), () => config)
    : afterHold(config));

  // The session is known. Attach the cached token — or, when the user IS signed in but the cached token is unusable (expired,
  // inside the skew, blank) and this request would carry it, wait (bounded) for a fresh one instead of going out tokenless.
  function finish(config) {
    try {
      const ctx = getCtx();
      if (refresh && needsFreshToken(config, ctx, apiBase)) {
        return boundedWait(refresh, refreshCapMs, setTimer, clearTimer).then((fresh) => {
          try {
            const now = getCtx();
            return applyAuthHeader(config, fresh && fresh.token
              ? Object.assign({}, now, { token: fresh.token, expiresAt: fresh.expiresAt, signedIn: true })
              : now);
          } catch (_e) {
            return config;
          }
        }, () => config);
      }
      return applyAuthHeader(config, ctx);
    } catch (_e) {
      return config;
    }
  }

  const afterHold = (config) => (holding() ? hold.wait().then(() => finish(config), () => config) : finish(config));
  return function handleRequest(config) {
    try {
      if (!gate.isSettled() && wouldAttachIfSignedIn(config, apiBase)) {
        return gate.wait().then((how) => afterGate(config, how), () => config);
      }
      if (holding() && wouldAttachIfSignedIn(config, apiBase)) return afterHold(config);
      return finish(config);
    } catch (_e) {
      return config;
    }
  };
}

// Signed in, request would be tagged, but the cached token cannot be attached right now. A signed-OUT tab (nobody to refresh) and a
// request the policy never tags are never asked to wait.
function needsFreshToken(config, ctx, apiBase) {
  const x = ctx || {};
  if (!x.signedIn) return false;
  const d = decideAuthHeader(config, x);
  if (d.attach || (d.reason !== 'no_token' && d.reason !== 'token_expiring')) return false;
  return wouldAttachIfSignedIn(config, apiBase);
}

// Resolves with refresh()'s value, or undefined once `capMs` has passed or refresh() fails/throws. Never rejects.
function boundedWait(refresh, capMs, setTimer, clearTimer) {
  return new Promise((resolve) => {
    let done = false;
    let timer = null;
    const end = (v) => { if (done) return; done = true; if (timer !== null) clearTimer(timer); resolve(v); };
    timer = setTimer(() => end(undefined), capMs);
    try { Promise.resolve(refresh()).then(end, () => end(undefined)); } catch (_e) { end(undefined); }
  });
}
